import '@dataroom/ui/styles.css';
import './shell/shell.css';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ToastProvider } from '@dataroom/ui';
import { setUnauthenticatedHandler } from './lib/api';
import { queryClient } from './lib/queries';
import { routeTree } from './routeTree.gen';

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

setUnauthenticatedHandler(() => {
  const { href } = router.state.location;
  // Every route under the `_public` layout (sign-in, setup, status, public forms, tasks and pages
  // published to the web, the presenter window, the dev labs) works without a session: a background
  // 401 there (an AI status probe, a presence ping) never sends the visitor to the sign-in screen.
  if (isOnPublicRoute()) return;
  queryClient.removeQueries({ queryKey: ['me'] });
  void router.navigate({ to: '/login', search: { redirect: href } });
});

function isOnPublicRoute(): boolean {
  const { pathname } = router.state.location;
  if (router.state.matches.some((m) => m.routeId === '/_public' || m.routeId.startsWith('/_public/')))
    return true;
  // Before the first match resolves: the token routes and the sign-in screen by path.
  return /^\/(?:login|setup|status|f|p|task|present|dev)(?:\/|$)/.test(pathname);
}

// iOS zooms the page into any field it focuses whose text is under 16 px, and most fields here are
// 13-14 px. `maximum-scale=1` stops that zoom; Safari still lets the user pinch-zoom (it ignores the
// cap for gestures since iOS 10), so it is set on iOS only: Android would honour it and block pinch.
// iPadOS reports itself as a Mac, so a touch-capable "Mac" counts as iOS.
function capIosFocusZoom() {
  const ios =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const viewport = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  if (!ios || !viewport || viewport.content.includes('maximum-scale')) return;
  viewport.content += ', maximum-scale=1';
}
capIosFocusZoom();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
